import { Theme, useTheme } from "@amx/ux-twoui-theming";
import { HighContrastSelector, IStackStyles, IStyle, Stack as FluentStack } from "@fluentui/react";
import React from "react";
import { validateProps } from "../../../shared/props";
import { boxShadowDepths, useViewport } from "../../../styles";
import { SlotStack, SlotStackItem } from "../../Stacks/SlotStack/SlotStack";
import { Stack, StackItem } from "../../Stacks/Stack/Stack";
import { BackgroundType, BorderRadiusVariant, FeatureHeight, FeatureLayoutProps, InsidePaddingVariant, PaddingVariant } from "../Feature.types";

export const backgroundHeightMapping: { [k in FeatureHeight]: number } = {
    extraSmall: 48,
    small: 168,
    medium: 224,
    large: 300,
    extraLarge: 400
};

export const defaultBorderRadius = "8px";

const isCard = (backgroundType: BackgroundType) => {
    return backgroundType === "card" || backgroundType === "alertCard";
};

const getBackgroundStyles = (theme: Theme, backgroundType: BackgroundType, height: FeatureHeight, showShadow: boolean, borderRadius: BorderRadiusVariant): IStackStyles => {
    const root: IStyle = {
        backgroundColor: theme.palette.neutralHigh,
        borderRadius,
        boxShadow: showShadow ? boxShadowDepths.rest : undefined,
        minHeight: backgroundHeightMapping[height],
        overflow: "hidden",
        selectors: {
            [HighContrastSelector]: {
                outline: "1px solid transparent"
            }
        },
    };

    if (backgroundType === "card") {
        return { root };
    } else if (backgroundType === "alertCard") {
        return {
            root: {
                ...root,
                backgroundColor: theme.palette.redLight,
            }
        };
    } else {
        return {
            root: {
                minHeight: backgroundHeightMapping[height]
            }
        };
    }
};

const calculatePaddingVariant = (isFirstSlot: boolean, paddingVariant: PaddingVariant = "0px", insidePaddingVariant: InsidePaddingVariant = "0px") => {
    const padding = paddingVariant === "0px" ? "0px" : "16px";
    if (isFirstSlot) {
        return `${padding} ${insidePaddingVariant} ${padding} ${paddingVariant}`;
    }
    return `${padding} ${padding} ${padding} ${insidePaddingVariant}`;
};

const getPaddingForImageLayout = (
    backgroundType: BackgroundType,
    isMobile: boolean,
    isImageFullBleed: boolean,
    paddingVariant?: PaddingVariant,
    isImageBlockLeft?: boolean
) => {
    if (isImageFullBleed) {
        return;
    }
    if (paddingVariant) {
        // Padding variants applies to viewport >= 640. Below this size,
        // padding should switch to 16px (or 0px as appropriate).
        if (isMobile) {
            if (isCard(backgroundType)) {
                return "16px";
            }
            return "16px 0";
        }
        return calculatePaddingVariant(!!isImageBlockLeft, paddingVariant);
    }
    // Following are default paddings if padding variant is not specified
    if (isCard(backgroundType)) {
        if (isMobile) {
            return "16px 32px";
        }
        if (isImageBlockLeft) {
            return "16px 0 16px 32px";
        }
        return "16px 32px 16px 0";
    }
    return "16px 0";
};

const getPaddingForContentLayout = (
    backgroundType: BackgroundType,
    isMobile: boolean,
    paddingVariant?: PaddingVariant,
    isImageBlockLeft?: boolean,
    insidePaddingVariant?: InsidePaddingVariant
) => {
    if (paddingVariant || insidePaddingVariant) {
        // Padding variants applies to viewport >= 640. Below this size,
        // padding should switch to 16px (or 0px as appropriate).
        if (isMobile) {
            if (isCard(backgroundType)) {
                return "0 16px 16px";
            }
            return "0 0 16px";
        }
        return calculatePaddingVariant(!isImageBlockLeft, paddingVariant, insidePaddingVariant);
    }
    // Following are default paddings if padding variant is not specified
    if (isCard(backgroundType)) {
        if (isMobile) {
            return "0 32px 16px";
        }
        if (isImageBlockLeft) {
            return "16px 32px 16px 0";
        }
        return "16px 0 16px 32px";
    }
    return "16px 0";
};

export const FeatureLayout: React.FC<FeatureLayoutProps> = ({
    backgroundType = "card",
    layoutType,
    isImageBlockLeft,
    isImageBlockBottom,
    insidePaddingVariant,
    height = "medium",
    paddingVariant,
    showShadow = true,
    borderRadius = defaultBorderRadius,
    isImageFullBleed = false,
    imageSlot,
    contentSlot,
    isVerticalLayout = ["s", "m"],
    ...otherProps
}) => {

    validateProps("FeatureLayout", otherProps);

    const viewportType = useViewport();
    const theme = useTheme();

    const verticalLayout = isVerticalLayout.includes(viewportType);

    let stack: JSX.Element;
    if (verticalLayout) {
        stack = (
            <Stack reversed={isImageBlockBottom}>
                <StackItem padding={getPaddingForImageLayout(backgroundType, verticalLayout, isImageFullBleed, paddingVariant)}>
                    {imageSlot}
                </StackItem>
                <StackItem padding={getPaddingForContentLayout(backgroundType, verticalLayout, paddingVariant)}>
                    {contentSlot}
                </StackItem>
            </Stack>
        );
    } else {
        // tslint:disable: no-magic-numbers
        const totalSlotCount = 12;
        const firstSlotCount = layoutType === "2/3" ? 7 : 6;
        const gapSlotCount = layoutType === "2/3" ? 1 : 0;
        const secondSlotCount = (totalSlotCount - firstSlotCount) - gapSlotCount;
        stack = (
            <SlotStack totalSlots={totalSlotCount} verticalAlign="center" reversed={isImageBlockLeft}>
                <SlotStackItem slots={firstSlotCount}>
                    <Stack padding={getPaddingForContentLayout(backgroundType, verticalLayout, paddingVariant, isImageBlockLeft, insidePaddingVariant)}>
                        {contentSlot}
                    </Stack>
                </SlotStackItem>
                {gapSlotCount && (
                    <SlotStackItem slots={gapSlotCount}>
                        <></>
                    </SlotStackItem>
                )}
                <SlotStackItem slots={secondSlotCount}>
                    <Stack padding={getPaddingForImageLayout(backgroundType, verticalLayout, isImageFullBleed, paddingVariant, isImageBlockLeft)}>
                        {imageSlot}
                    </Stack>
                </SlotStackItem>
            </SlotStack>
        );
    }
    return (
        <FluentStack styles={getBackgroundStyles(theme, backgroundType, height, showShadow, borderRadius)} {...otherProps}>
            {stack}
        </FluentStack>
    );
};
